import { useState, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Trash2, Tag, PiggyBank, Pencil, HelpCircle, AlertCircle } from 'lucide-react';
import { FieldError, InputAdornment } from '@/components/glitch-ui/field-error';
import { BusinessLandingPageCombobox } from '@/components/glitch-ui/business-landing-page-combobox';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CharacterCounter } from '@/components/ui/character-counter';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/useToast';
import { useUpdatePriceMutationOptions, useDeletePriceMutationOptions } from '@/lib/query-options';
import type { CampaignData, PriceType } from '@/lib/types';
import { useRefetchCampaign } from '@/hooks/useRefetchCampaign';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const PRICE_TYPES = [
  { value: 'brands', label: 'Brands' },
  { value: 'events', label: 'Events' },
  { value: 'locations', label: 'Locations' },
  { value: 'neighborhoods', label: 'Neighborhoods' },
  { value: 'product categories', label: 'Product Categories' },
  { value: 'product tiers', label: 'Product Tiers' },
  { value: 'services', label: 'Services' },
  { value: 'service tiers', label: 'Service Tiers' },
  { value: 'service_categories', label: 'Service Categories' },
];

const PRICE_QUALIFIERS = [
  { value: 'no qualifier', label: 'None' },
  { value: 'from', label: 'From' },
  { value: 'up to', label: 'Up To' },
  { value: 'average', label: 'Average' },
];

const PRICE_FREQUENCIES = [
  { value: 'no units', label: 'No Units' },
  { value: 'per hour', label: 'Per Hour' },
  { value: 'per day', label: 'Per Day' },
  { value: 'per week', label: 'Per Week' },
  { value: 'per month', label: 'Per Month' },
  { value: 'per year', label: 'Per Year' },
  { value: 'per night', label: 'Per Night' },
];

const EMPTY_FORM = {
  type: 'services',
  currency: 'USD',
  qualifier: 'no qualifier',
  header: '',
  value: '',
  frequency: 'no units',
  description: '',
  final_url: '',
  mobile_url: '',
};

interface PriceSectionProps {
  campaign: CampaignData;
  disabled?: boolean;
}

export interface PriceSectionRef {
  openAdd: () => void;
}

export const PriceSection = forwardRef<PriceSectionRef, PriceSectionProps>(({ campaign, disabled = false }, ref) => {
  const { id: campaignId } = campaign;
  const refetchCampaign = useRefetchCampaign(campaign.slug);
  const { toast } = useToast();

  const [deletingPrice, setDeletingPrice] = useState<PriceType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState<PriceType | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // Tell-on-click (#5810): Save stays enabled; clicking it marks each missing/invalid
  // field (red border + ! icon + message beside the label) instead of sitting
  // silently disabled.
  type PriceFieldError = Partial<Record<'header' | 'description' | 'value' | 'final_url', string>>;
  const [fieldErrors, setFieldErrors] = useState<PriceFieldError>({});

  // #4449: new price rows default to the org's configured currency (fallback USD); existing rows
  // keep their stored currency, and the user can still override per row.
  const organizationData = useAtomValue(organizationDataAtom);
  const defaultCurrency = organizationData?.default_currency || 'USD';

  const updateMutation = useUpdatePriceMutationOptions();
  const deleteMutation = useDeletePriceMutationOptions();

  const prices = campaign.prices || [];

  const underfilledTypes = useMemo(() => {
    const counts = prices.reduce<Record<string, number>>((acc, p) => {
      acc[p.type] = (acc[p.type] ?? 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts)
      .filter(([, n]) => n >= 1 && n <= 2)
      .map(([type, n]) => ({
        type,
        label: PRICE_TYPES.find(t => t.value === type)?.label ?? type,
        count: n,
      }));
  }, [prices]);

  const setField = (field: keyof typeof EMPTY_FORM, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => (field in prev ? { ...prev, [field]: undefined } : prev));
  };

  const isValidUrl = (url: string): boolean => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const openAddModal = () => {
    setEditingPrice(null);
    setForm({ ...EMPTY_FORM, currency: defaultCurrency });
    setFieldErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = useCallback((price: PriceType) => {
    setEditingPrice(price);
    setForm({
      type: price.type,
      currency: price.currency,
      qualifier: price.qualifier,
      header: price.header,
      value: String(price.value),
      frequency: price.frequency,
      description: price.description,
      final_url: price.final_url,
      mobile_url: price.mobile_url || '',
    });
    setFieldErrors({});
    setIsModalOpen(true);
  }, []);

  const handleSave = async () => {
    let normalizedUrl = form.final_url.trim();
    if (normalizedUrl && !/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    const parsedValue = parseFloat(form.value);

    const errors: PriceFieldError = {};
    if (!form.header.trim()) errors.header = 'Required';
    if (!form.description.trim()) errors.description = 'Required';
    if (!form.value) errors.value = 'Required';
    else if (isNaN(parsedValue) || parsedValue < 0) errors.value = 'Must be 0 or more';
    if (!normalizedUrl) errors.final_url = 'Required';
    else if (!isValidUrl(normalizedUrl)) errors.final_url = 'Enter a valid URL';
    if (Object.values(errors).some(Boolean)) {
      setFieldErrors(errors);
      return;
    }

    const price: PriceType = {
      ...(editingPrice ? { id: editingPrice.id } : {}),
      type: form.type,
      currency: form.currency,
      qualifier: form.qualifier,
      header: form.header.trim(),
      value: parsedValue,
      frequency: form.frequency,
      description: form.description.trim(),
      final_url: normalizedUrl,
      mobile_url: form.mobile_url.trim() || undefined,
    };

    try {
      await updateMutation.mutateAsync({ campaignId, price });
      setIsModalOpen(false);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to save price',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPrice?.id) return;
    try {
      await deleteMutation.mutateAsync({ campaignId, priceId: deletingPrice.id });
      setDeletingPrice(null);
      refetchCampaign();
    } catch (error) {
      console.error(error);
      toast({
        description: error instanceof Error ? error.message : 'Failed to delete price',
        variant: 'destructive',
      });
    }
  };

  const formatPrice = (price: PriceType) => {
    const qualifier = price.qualifier !== 'no qualifier' ? `${price.qualifier} ` : '';
    const frequency = price.frequency !== 'no units' ? ` / ${price.frequency.replace('per ', '')}` : '';
    return `${qualifier}${price.currency} ${price.value.toLocaleString()}${frequency}`;
  };

  const columns: ColumnDef<PriceType, unknown>[] = useMemo(() => [
    {
      accessorKey: 'header',
      header: 'Price Item',
      cell: ({ row }) => (
        <div className="flex items-start gap-2">
          <Tag className="h-3.5 w-3.5 text-gray-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.header}</div>
            <div className="text-xs text-gray-500">{row.original.description}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'value',
      header: 'Price',
      cell: ({ row }) => (
        <div className="text-sm text-gray-900">{formatPrice(row.original)}</div>
      ),
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const label = PRICE_TYPES.find(t => t.value === row.original.type)?.label ?? row.original.type;
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-600">
            {label}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex justify-end gap-1">
          {!disabled && (
            <>
              <IconButton variant="default" onClick={() => openEditModal(row.original)}>
                <Pencil className="h-4 w-4" />
              </IconButton>
              <IconButton variant="destructive" onClick={() => setDeletingPrice(row.original)}>
                <Trash2 className="h-4 w-4" />
              </IconButton>
            </>
          )}
        </div>
      ),
    },
  ], [disabled, openEditModal]);

  // Only disable while a save is in flight — missing fields are explained on click
  // via formError instead of silently disabling the button (#5810).
  const isSaveDisabled = updateMutation.isPending;

  useImperativeHandle(ref, () => ({ openAdd: openAddModal }), [openAddModal]);

  return (
    <div>
      {underfilledTypes.length > 0 && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-lg border-l-4 bg-[#FEF3C7] border-[#FDE68A] text-[#A16207] text-sm">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            {underfilledTypes
              .map(({ label, count }) => `“${label}” has ${count} price${count === 1 ? '' : 's'}`)
              .join('; ')}
            . At least 3 of a type are needed to publish to Google Ads.
          </span>
        </div>
      )}

      <DataTable<PriceType>
        data={[...prices].reverse()}
        columns={columns}
        variant="bordered"
        pageSize={999}
        emptyIcon={PiggyBank}
        emptyTitle="No Prices"
        emptyDescription="Show specific pricing information to help customers make decisions."
      />

      {/* Add / Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{editingPrice ? 'Edit Price' : 'Add Price'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Type */}
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setField('type', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Header */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Header</Label>
                <FieldError message={fieldErrors.header} />
              </div>
              <div className="relative mt-1">
                <Input
                  value={form.header}
                  onChange={(e) => setField('header', e.target.value)}
                  maxLength={25}
                  placeholder="e.g. Basic Plan"
                  autoFocus
                  error={!!fieldErrors.header}
                  className={fieldErrors.header ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.header}>
                  <CharacterCounter current={form.header.length} max={25} variant="compact" />
                </InputAdornment>
              </div>
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Description</Label>
                <FieldError message={fieldErrors.description} />
              </div>
              <div className="relative mt-1">
                <Input
                  value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  maxLength={25}
                  placeholder="e.g. Everything you need to get started"
                  error={!!fieldErrors.description}
                  className={fieldErrors.description ? 'pr-20' : 'pr-14'}
                />
                <InputAdornment error={!!fieldErrors.description}>
                  <CharacterCounter current={form.description.length} max={25} variant="compact" />
                </InputAdornment>
              </div>
            </div>

            {/* Price row: Currency + Value + Qualifier + Frequency */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Currency</Label>
                <Input
                  value={form.currency}
                  onChange={(e) => setField('currency', e.target.value.toUpperCase())}
                  maxLength={3}
                  placeholder="USD"
                  className="mt-1"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Amount</Label>
                  <FieldError message={fieldErrors.value} />
                </div>
                <div className="relative mt-1">
                  <Input
                    type="text"
                    // text + inputMode keeps the numeric keypad while letting us refuse
                    // non-numeric keystrokes at entry — type="number" silently exposes ''
                    // for junk like a stray 'e', which made the error read "Required"
                    // while the user saw text in the box (#5857).
                    inputMode="decimal"
                    value={form.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) setField('value', val);
                    }}
                    placeholder="99.00"
                    error={!!fieldErrors.value}
                    className={fieldErrors.value ? 'pr-8' : ''}
                  />
                  <InputAdornment error={!!fieldErrors.value} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qualifier</Label>
                <Select value={form.qualifier} onValueChange={(v) => setField('qualifier', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_QUALIFIERS.map(q => (
                      <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={form.frequency} onValueChange={(v) => setField('frequency', v)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_FREQUENCIES.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Final URL */}
            <div>
              <div className="flex items-center gap-1.5">
                <Label>Final URL</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="What is Final URL?">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      Where users land when they click this price row in your ad.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {fieldErrors.final_url && (
                  <span className="ml-auto">
                    <FieldError message={fieldErrors.final_url} />
                  </span>
                )}
              </div>
              {/* Landing-page picker per the URL Field Pattern (#5855). */}
              <div className={fieldErrors.final_url ? 'relative mt-1 rounded-md ring-1 ring-red-500' : 'relative mt-1'}>
                <BusinessLandingPageCombobox
                  selectedUrl={form.final_url || null}
                  onSelect={(entry) => setField('final_url', entry?.url ?? '')}
                  placeholder="Select or add a landing page URL..."
                />
                <InputAdornment error={!!fieldErrors.final_url} />
              </div>
            </div>

            {/* Mobile URL */}
            <div>
              <div className="flex items-center gap-1.5">
                <Label>Mobile URL <span className="text-gray-400 font-normal">(optional)</span></Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="What is Mobile URL?">
                        <HelpCircle className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      Used when your ad is served on a mobile device. Defaults to Final URL when blank.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="mt-1">
                <BusinessLandingPageCombobox
                  selectedUrl={form.mobile_url || null}
                  onSelect={(entry) => setField('mobile_url', entry?.url ?? '')}
                  placeholder="Optional. Select or add a mobile URL..."
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} className="leading-none">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled}
              className="bg-[#290BD9] hover:bg-[#2309C5] leading-none"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConformationModal
        isOpen={!!deletingPrice}
        onClose={() => setDeletingPrice(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Price"
        description={`Are you sure you want to delete "${deletingPrice?.header}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
});
PriceSection.displayName = 'PriceSection';
